import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { invokeFunction } from '@/lib/invoke'
import { Button } from '@/components/ui'

/**
 * Resend and Revoke for one pending invitation. Both go through the
 * invite-user Edge Function: resend needs Auth to issue a fresh link, and
 * revoke has to delete the not-yet-confirmed auth user as well as the
 * invitation row, or the link already in the invitee's inbox keeps working.
 */
export function InvitationActions({
  invitation,
  onChanged,
  onError,
}: {
  invitation: { id: string; email: string }
  onChanged: () => void
  onError: (message: string) => void
}) {
  const [resent, setResent] = useState(false)

  const resend = useMutation({
    mutationFn: () => invokeFunction('invite-user', { action: 'resend', invitationId: invitation.id }),
    onSuccess: () => {
      setResent(true)
      onChanged()
    },
    onError: (e: Error) => onError(e.message || 'That invitation could not be resent.'),
  })

  const revoke = useMutation({
    mutationFn: () => invokeFunction('invite-user', { action: 'revoke', invitationId: invitation.id }),
    onSuccess: onChanged,
    onError: (e: Error) => onError(e.message || 'That invitation could not be revoked.'),
  })

  const busy = resend.isPending || revoke.isPending

  return (
    <span className="inline-flex gap-3 whitespace-nowrap">
      <Button
        variant="quiet"
        className="px-0"
        disabled={busy || resent}
        onClick={() => resend.mutate()}
      >
        {resend.isPending ? 'Sending…' : resent ? 'Sent' : 'Resend'}
      </Button>
      <Button
        variant="quiet"
        className="px-0"
        disabled={busy}
        onClick={() => {
          if (window.confirm(`Revoke the invitation for ${invitation.email}? The link they were sent will stop working.`)) {
            revoke.mutate()
          }
        }}
      >
        {revoke.isPending ? 'Revoking…' : 'Revoke'}
      </Button>
    </span>
  )
}
